import { StateCreator } from "zustand";
import { VendState } from ".";
import {
  fetchMachines,
  fetchMachine,
  createMachine as createMachineApi,
  editMachine as editMachineApi,
  deleteMachine as deleteMachineApi,
  activateMachine as activateMachineApi,
  deactivateMachine as deactivateMachineApi,
} from "../api/machine/api";
import { fetchLocations } from "../api/location/api";
import { Machine } from "../types";
import { getLocationId, machineTypeLabel, withWriteAction } from "../utils";
import { useUserStore } from "../../user/state/store";
import { runLoad } from "./helpers";
import { useBillingActionModalStore } from "./billingActionModal";
import { taskScopeKeys } from "../taskScopes";
import { resetMachineCollectionSummaryCache } from "../liveSales/store";

export type MachineSlice = {
  machines: Machine[];
  availableMachines: Machine[];
  machinesLoading: boolean;
  machinesHydrated: boolean;
  machinesFetchedAt: number | null;
  setMachines: (machines: Machine[]) => void;
  setAvailableMachines: (machines: Machine[]) => void;
  addMachine: (machine: Machine) => void;
  loadMachines: (options?: { force?: boolean }) => Promise<void>;
  loadAvailableMachines: () => Promise<void>;
  fetchMachine: (id: string) => Promise<Machine>;
  refreshMachinesByIds: (ids: string[]) => Promise<void>;
  createMachine: (machine: Machine) => Promise<Machine>;
  editMachine: (machine: Machine) => Promise<Machine>;
  activateMachine: (machineId: string) => Promise<Machine>;
  deactivateMachine: (machineId: string) => Promise<Machine>;
  deleteMachine: (id: string) => Promise<{ id: string }>;
  assignMachineToLocation: (
    machineId: string,
    locationId: string
  ) => Promise<Machine>;
  unassignMachine: (machineId: string) => Promise<Machine>;
};

export const deriveAvailable = (machines: Machine[]) =>
  machines.filter((m) => !getLocationId(m.location as any));

let machinesPromise: Promise<void> | null = null;
let machinesLoadGeneration = 0;

export const resetMachineLoadState = () => {
  machinesPromise = null;
  machinesLoadGeneration += 1;
};

const normalizeMachines = (data: Machine[]) =>
  data.map((m) => ({
    ...m,
    type: machineTypeLabel(m.type),
  }));

const replaceMachine = (machines: Machine[], updated: Machine) => {
  const normalized = normalizeMachines([updated])[0];
  if (!normalized.id) return normalizeMachines(machines);
  let found = false;
  const next = machines.map((machine) => {
    if (machine.id !== normalized.id) return machine;
    found = true;
    return normalized;
  });
  return found ? next : [...next, normalized];
};

const refreshTasksAndRoutePlanner = async (
  state: VendState,
  machineId?: string | null
) => {
  if (machineId) {
    await state.loadTasks?.({
      force: true,
      silent: true,
      filters: { machineId },
      scopeKey: taskScopeKeys.machine(machineId),
    });
    return;
  }
  await state.loadTasks?.({ force: true });
};

const showActivationChargeFailed = () => {
  window.setTimeout(() => {
    useBillingActionModalStore.getState().showActivationFailure();
  }, 0);
};

export const createMachineSlice: StateCreator<
  VendState,
  [],
  [],
  MachineSlice
> = (set, get) => ({
  machines: [],
  availableMachines: [],
  machinesLoading: false,
  machinesHydrated: false,
  machinesFetchedAt: null,
  setMachines: (machines) =>
    set({ machines, machinesFetchedAt: Date.now() } as VendState),
  setAvailableMachines: (availableMachines) =>
    set({ availableMachines } as VendState),
  addMachine: (machine) => {
    const state = get();
    const machines = [...state.machines, machine];
    set({
      machines,
      availableMachines: deriveAvailable(machines),
      machinesFetchedAt: Date.now(),
    } as VendState);
  },
  loadMachines: async (options) => {
    const state = get() as VendState;
    if (!options?.force && state.machinesHydrated) return;
    if (machinesPromise) return machinesPromise;
    const authUserIdAtStart = useUserStore.getState().id || "";
    const generationAtStart = machinesLoadGeneration;
    const request = (async () => {
      try {
        await runLoad({
          isLoading: () => (get() as VendState).machinesLoading,
          setLoading: (loading) =>
            set((current) => ({
              ...(current as VendState),
              machinesLoading: loading,
            })),
          fetcher: fetchMachines,
          onSuccess: (data) => {
            const currentUserId = useUserStore.getState().id || "";
            if (
              machinesLoadGeneration !== generationAtStart ||
              currentUserId !== authUserIdAtStart
            ) {
              return;
            }
            const mapped = data.map((m) => ({
              ...m,
              type: machineTypeLabel(m.type),
            }));
            set({
              machines: mapped,
              availableMachines: deriveAvailable(mapped),
              machinesFetchedAt: Date.now(),
            } as VendState);
          },
        });
      } finally {
        const currentUserId = useUserStore.getState().id || "";
        if (
          machinesLoadGeneration === generationAtStart &&
          currentUserId === authUserIdAtStart
        ) {
          set({ machinesHydrated: true } as VendState);
        }
        machinesPromise = null;
      }
    })();
    machinesPromise = request;
    return request;
  },
  loadAvailableMachines: async () => {
    const state = get();
    if ((state.machines || []).length === 0) {
      await state.loadMachines();
      return;
    }
    set({ availableMachines: deriveAvailable(state.machines) } as VendState);
  },
  fetchMachine: async (id: string) => {
    return fetchMachine(id);
  },
  refreshMachinesByIds: async (ids) => {
    const uniqueIds = Array.from(new Set(ids.filter(Boolean)));
    if (!uniqueIds.length) return;
    const results = await Promise.allSettled(
      uniqueIds.map((id) => fetchMachine(id))
    );
    const refreshed = results.flatMap((result) =>
      result.status === "fulfilled" ? [result.value] : []
    );
    if (!refreshed.length) {
      const failure = results.find(
        (result): result is PromiseRejectedResult => result.status === "rejected"
      );
      throw failure?.reason || new Error("Failed to refresh machines");
    }
    set((current) => {
      let machines = current.machines;
      refreshed.forEach((machine) => {
        machines = replaceMachine(machines, machine);
      });
      return {
        machines,
        availableMachines: deriveAvailable(machines),
      } as VendState;
    });
  },
  createMachine: async (machine) => {
    return await withWriteAction(
      "Creating Machine",
      async () => {
      const created = await createMachineApi(machine);
      if (created.id) resetMachineCollectionSummaryCache([created.id]);
      const machines = replaceMachine((get() as VendState).machines, created);
      set({
        machines,
        availableMachines: deriveAvailable(machines),
        machinesFetchedAt: Date.now(),
      } as VendState);
      if (typeof (get() as VendState).setLocations === "function") {
        const locations = await fetchLocations();
        (get() as VendState).setLocations(locations);
      }
      if (getLocationId(created.location as any)) {
        refreshTasksAndRoutePlanner(get() as VendState, created.id).catch(() => {});
      }
      useUserStore.getState().refreshSubscription({ force: true }).catch(() => {});
      if ((created as any).activationChargeFailed) {
        showActivationChargeFailed();
      }
      return created;
      }
    );
  },
  editMachine: async (machine) => {
    return await withWriteAction("Saving Machine", async () => {
      const state = get() as VendState;
      const current = state.machines.find((m) => m.id === machine.id);
      const previousLocationId = getLocationId(current?.location as any);
      const updated = await editMachineApi(machine);
      if (updated.id) resetMachineCollectionSummaryCache([updated.id]);
      const nextLocationId = getLocationId(updated.location as any);
      const machines = replaceMachine((get() as VendState).machines, updated);
      set({
        machines,
        availableMachines: deriveAvailable(machines),
        machinesFetchedAt: Date.now(),
      } as VendState);
      if (
        previousLocationId !== nextLocationId &&
        typeof (get() as VendState).setLocations === "function"
      ) {
        const locations = await fetchLocations();
        (get() as VendState).setLocations(locations);
      }
      refreshTasksAndRoutePlanner(get() as VendState, updated.id).catch(() => {});
      return updated;
    });
  },
  activateMachine: async (machineId) => {
    return await withWriteAction("Activating Machine", async () => {
      const updated = await activateMachineApi(machineId);
      resetMachineCollectionSummaryCache(updated.id ? [updated.id] : [machineId]);
      const machines = replaceMachine((get() as VendState).machines, updated);
      set({
        machines,
        availableMachines: deriveAvailable(machines),
        machinesFetchedAt: Date.now(),
      } as VendState);
      await refreshTasksAndRoutePlanner(get() as VendState, updated.id);
      useUserStore.getState().refreshSubscription({ force: true }).catch(() => {});
      if ((updated as any).activationChargeFailed) {
        showActivationChargeFailed();
      }
      return updated;
    });
  },
  deactivateMachine: async (machineId) => {
    return await withWriteAction("Deactivating Machine", async () => {
      const updated = await deactivateMachineApi(machineId);
      resetMachineCollectionSummaryCache(updated.id ? [updated.id] : [machineId]);
      const machines = replaceMachine((get() as VendState).machines, updated);
      set({
        machines,
        availableMachines: deriveAvailable(machines),
        machinesFetchedAt: Date.now(),
      } as VendState);
      refreshTasksAndRoutePlanner(get() as VendState, updated.id).catch(() => {});
      useUserStore.getState().refreshSubscription({ force: true }).catch(() => {});
      return updated;
    });
  },
  deleteMachine: async (id: string) => {
    return await withWriteAction("Deleting Machine", async () => {
      const result = await deleteMachineApi(id);
      resetMachineCollectionSummaryCache([id]);
      const machines = normalizeMachines(await fetchMachines());
      set({
        machines,
        availableMachines: deriveAvailable(machines),
        machinesFetchedAt: Date.now(),
      } as VendState);
      if (typeof (get() as VendState).setLocations === "function") {
        const locations = await fetchLocations();
        (get() as VendState).setLocations(locations);
      }
      refreshTasksAndRoutePlanner(get() as VendState).catch(() => {});
      useUserStore.getState().refreshSubscription({ force: true }).catch(() => {});
      return result;
    });
  },
  assignMachineToLocation: async (machineId, locationId) => {
    return await withWriteAction("Assigning Machine", async () => {
      const state = get();
      const machine = state.machines.find((m) => m.id === machineId);
      if (!machine) throw new Error("Machine not found");
      const updated = await editMachineApi({
        ...machine,
        location: locationId,
        assigned: true,
      });
      resetMachineCollectionSummaryCache([machineId]);
      const machines = replaceMachine((get() as VendState).machines, updated);
      set({
        machines,
        availableMachines: deriveAvailable(machines),
        machinesFetchedAt: Date.now(),
      } as VendState);
      if (typeof (get() as VendState).setLocations === "function") {
        const locations = await fetchLocations();
        (get() as VendState).setLocations(locations);
      }
      refreshTasksAndRoutePlanner(get() as VendState, updated.id).catch(() => {});
      return updated;
    });
  },
  unassignMachine: async (machineId) => {
    return await withWriteAction("Unassigning Machine", async () => {
      const state = get();
      const machine = state.machines.find((m) => m.id === machineId);
      if (!machine) throw new Error("Machine not found");
      const updated = await editMachineApi({
        ...machine,
        location: null,
        assigned: false,
      });
      resetMachineCollectionSummaryCache([machineId]);
      const machines = replaceMachine((get() as VendState).machines, updated);
      set({
        machines,
        availableMachines: deriveAvailable(machines),
        machinesFetchedAt: Date.now(),
      } as VendState);
      if (typeof (get() as VendState).setLocations === "function") {
        const locations = await fetchLocations();
        (get() as VendState).setLocations(locations);
      }
      refreshTasksAndRoutePlanner(get() as VendState, updated.id).catch(() => {});
      return updated;
    });
  },
});
